import type {ComponentPropsWithRef} from 'react';
import React, {Children, cloneElement} from 'react';
import mergeRefs from 'merge-refs';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './table.module.styl';

const classNames = withCssModule(styles);

export type RowProps = ComponentPropsWithRef<'tr'> & {
    /**
     * If true, cells in this row render as `<th>` elements
     */
    isHeaderRow?: boolean,
    /**
     * @private
     * If true, cells in this row render as `<th>` elements
     * @deprecated Use `isHeaderRow` instead. It will be removed in 6 months 05-03-2026.
     */
    th?: boolean,
    /**
     * Set true to show custom zebra striping
     */
    showCustomZebra?: boolean,
    /**
     * Set true to show zebra striping
     * @default true
     */
    showZebra?: boolean,
    /**
     * Show cell top border
     * @default true
     */
    cellBorder?: boolean,
    /**
     * Highlight all cells in the row
     * @default false
     */
    highlighted?: boolean,
    /**
     * Mix for Row
     * @deprecated Use `className` instead. It will be removed in 6 months 03-03-2026.
     */
    mix?: string,
    /**
     * Callback ref for the `<tr>` element
     * @deprecated Use `ref` instead. It will be removed in 6 months 03-03-2026.
     */
    catchRef?: (el: HTMLTableRowElement | null) => void,
};

export const Row = ({
    isHeaderRow = false,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    th,
    showCustomZebra,
    showZebra = true,
    cellBorder = true,
    highlighted = false,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    mix,
    className,
    children,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    catchRef,
    ref,
    ...rest
}: RowProps) => {
    const isCaption = (child: React.ReactElement<{caption?: boolean}>) =>
        child && child.props.caption;

    const isHeader = isHeaderRow || (th ?? false);

    const rowClassName = classNames(
        'table__row',
        mix,
        className,
        {
            table__row_head: isHeader,
            table__row_body: !isHeader,
            table__row_body_zebra: showZebra && showCustomZebra === undefined,
            table__row_body_custom_zebra: showCustomZebra,
            table__row_border: !cellBorder,
            table__row_highlighted: highlighted,
            table__row_caption: Children.toArray(children).some(
                child => React.isValidElement(child) && isCaption(child as React.ReactElement<{caption?: boolean}>),
            ),
        },
    );

    const mappedChildren = isHeader
        ? Children.map(children, child => {
                if (!child) {
                    return null;
                }
                return cloneElement(child as React.ReactElement<{as?: 'th' | 'td'}>, {as: 'th'});
            })
        : children;

    return (
        <tr
            {...rest}
            ref={mergeRefs(ref, catchRef)}
            className={rowClassName}>
            {mappedChildren}
        </tr>
    );
};
